import React, {
  useEffect,
  useContext,
  useReducer,
  createContext,
  FC,
  Reducer,
} from 'react';
import { reducerWithLogger } from './util';
import api, { Player, PlayersMap, PlayerGroupsMap } from './players';
import AccountContext from './AccountContext';

enum Action {
  SelectPlayer = 'SELECT_PLAYER',
  SetPlayer = 'SET_PLAYER',
  CreatePlayer = 'CREATE_PLAYER',
  CreatePlayerSuccess = 'CREATE_PLAYER_SUCCESS',
  CreatePlayerFailure = 'CREATE_PLAYER_FAILURE',
  UpdatePlayer = 'UPDATE_PLAYER',
  UpdatePlayerSuccess = 'UPDATE_PLAYER_SUCCESS',
  UpdatePlayerFailure = 'UPDATE_PLAYER_FAILURE',
  FetchPlayers = 'FETCH_PLAYERS',
  FetchPlayersSuccess = 'FETCH_PLAYERS_SUCCESS',
  FetchPlayersFailure = 'FETCH_PLAYERS_FAILURE',
}

interface SelectPlayerAction {
  type: Action.SelectPlayer;
  playerId: string;
}

interface SetPlayerAction {
  type: Action.SetPlayer;
  player: Player;
}

interface CreatePlayerAction {
  type: Action.CreatePlayer;
}

interface CreatePlayerSuccessAction {
  type: Action.CreatePlayerSuccess;
  player: Player;
}

interface CreatePlayerFailureAction {
  type: Action.CreatePlayerFailure;
  error: Error;
}

interface UpdatePlayerAction {
  type: Action.UpdatePlayer;
}

interface UpdatePlayerSuccessAction {
  type: Action.UpdatePlayerSuccess;
  player: Player;
}

interface UpdatePlayerFailureAction {
  type: Action.UpdatePlayerFailure;
  error: Error;
}

interface FetchPlayersAction {
  type: Action.FetchPlayers;
}

interface FetchPlayersSuccessAction {
  type: Action.FetchPlayersSuccess;
  players: PlayersMap;
  groups: PlayerGroupsMap;
}

interface FetchPlayersFailureAction {
  type: Action.FetchPlayersFailure;
  error: Error;
}

type PlayersAction =
  | SelectPlayerAction
  | SetPlayerAction
  | CreatePlayerAction
  | CreatePlayerSuccessAction
  | CreatePlayerFailureAction
  | UpdatePlayerAction
  | UpdatePlayerSuccessAction
  | UpdatePlayerFailureAction
  | FetchPlayersAction
  | FetchPlayersSuccessAction
  | FetchPlayersFailureAction;

interface PlayersState {
  playersMap: PlayersMap;
  groupsMap: PlayerGroupsMap;
  loading: boolean;
  ready: boolean;
  selected?: string;
  error?: string;
}

type PlayersReducer = Reducer<PlayersState, PlayersAction>;

type PlayersContextProvider = PlayersState & {
  getSelected(): Player;
  count(): number;
  selectPlayer(playerId: string): void;
  setPlayer(player: Player): void;
  createPlayer: typeof api.create;
  updatePlayer: typeof api.update;
  fetchPlayers(): Promise<[PlayersMap, PlayerGroupsMap]>;
};

const initialState: PlayersState = {
  playersMap: {},
  groupsMap: {},
  loading: false,
  ready: false,
  selected: null,
  error: null,
};

const reducer = reducerWithLogger(
  (state: PlayersState, action: PlayersAction) => {
    switch (action.type) {
      case Action.SelectPlayer:
        return {
          ...state,
          selected: action.playerId,
        };

      case Action.SetPlayer:
        return {
          ...state,
          playersMap: {
            ...state.playersMap,
            [action.player.id]: action.player,
          },
        };

      case Action.CreatePlayerSuccess:
      case Action.UpdatePlayerSuccess:
        return {
          ...state,
          loading: false,
          error: null,
          playersMap: {
            ...state.playersMap,
            [action.player.id]: action.player,
          },
        };

      case Action.FetchPlayersSuccess:
        return {
          ...state,
          loading: false,
          error: null,
          ready: true,
          selected: Object.keys(action.players)[0] ?? null,
          playersMap: action.players,
          groupsMap: action.groups,
        };

      case Action.CreatePlayer:
      case Action.UpdatePlayer:
      case Action.FetchPlayers:
        return {
          ...state,
          loading: true,
        };

      case Action.CreatePlayerFailure:
      case Action.UpdatePlayerFailure:
      case Action.FetchPlayersFailure:
        return {
          ...state,
          loading: false,
          error: action.error.message,
        };

      default:
        return state;
    }
  }
);

const PlayersContext = createContext<Partial<PlayersContextProvider>>({});

const PlayersProvider: FC = props => {
  const [state, dispatch] = useReducer<PlayersReducer>(reducer, initialState);
  const account = useContext<any>(AccountContext);

  /**
   * Gets the selected player object.
   */
  const getSelected: PlayersContextProvider['getSelected'] = () => {
    return state.playersMap[state.selected];
  };
  /**
   * Counts the number of players.
   */
  const count: PlayersContextProvider['count'] = () => {
    return Object.keys(state.playersMap).length;
  };
  /**
   * Sets the player identified by playerId as the selected player.
   */
  const selectPlayer: PlayersContextProvider['selectPlayer'] = playerId => {
    dispatch({ type: Action.SelectPlayer, playerId });
  };
  /**
   * Replaces player data.
   */
  const setPlayer: PlayersContextProvider['setPlayer'] = player => {
    dispatch({ type: Action.SetPlayer, player });
  };
  /**
   * Creates and stores a new player.
   */
  const createPlayer: PlayersContextProvider['createPlayer'] = async params => {
    dispatch({ type: Action.CreatePlayer });

    const player = await api.create(params);
    dispatch({ type: Action.CreatePlayerSuccess, player });
    return player;
  };

  /**
   * Updates the player identified by id.
   */
  const updatePlayer: PlayersContextProvider['updatePlayer'] = async (
    id,
    params
  ) => {
    dispatch({ type: Action.UpdatePlayer });

    const player = await api.update(id, params);
    dispatch({ type: Action.UpdatePlayerSuccess, player });
    return player;
  };
  /**
   * Fetches and updates the list of all active players for the active account.
   */
  const fetchPlayers: PlayersContextProvider['fetchPlayers'] = async () => {
    dispatch({ type: Action.FetchPlayers });

    const [players, groups] = await api.getAll();
    dispatch({ type: Action.FetchPlayersSuccess, players, groups });
    return [players, groups];
  };

  useEffect(() => {
    (async (): Promise<void> => {
      if (account.ready) {
        await fetchPlayers();
      }
    })();
  }, [account.ready, account.memberId]);

  const value = {
    ...state,
    getSelected,
    count,
    setPlayer,
    createPlayer,
    updatePlayer,
    fetchPlayers,
    selectPlayer,
  };

  return (
    <PlayersContext.Provider value={value}>
      {props.children}
    </PlayersContext.Provider>
  );
};
PlayersProvider.displayName = 'PlayersProvider';

export { PlayersContext as default, PlayersProvider };
